import React, { useEffect, useLayoutEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { FiInfo, FiLock, FiPlayCircle } from 'react-icons/fi';
import styled from 'styled-components';

import { Title } from '../components/Title';
import { assetsUrl } from '../util/constants';
import { ShowSiteLoading } from '../SiteLoadingIndicator';
import { Table } from './Table';
import { Button } from '../components/Button';
import { ContentScreen } from '../styles/ContentScreen';

const WeekDisplay = styled.div`
   margin-bottom: 5em;

   h3 {
      font-size: 1.8em;
      margin-top: 1em;
      margin-bottom: 0.75em;
   }
   h4 {
      font-family: var(--heading-font);
      font-size: 1.5em;
      margin-top: 1em;
      margin-bottom: 0.75em;
   }

   ul {
      margin-left: 1em;
      list-style: disc;

      font-size: 1.25em;
      line-height: 1.7;

      a.button-link {
         text-decoration: none;
      }
   }

   .weekly-challenge-interactive-list {
      list-style: none;
      margin-left: 0;
   }

   .weekly-challenge-play-link {
      display: flex;
      align-items: center;

      svg {
         margin-right: 0.5em;
      }
   }

   .weekly-challenge-nav {
      margin-top: 2em;
      text-align: center;
      p {
         font-size: 1rem;
         flex-basis: 33%;

         margin-bottom: 1em;
      }
   }

   @media (min-width: 550px) {
      .weekly-challenge-nav {
         display: flex;

         p {
            margin-bottom: 0;
         }
      }
   }
`;

const WeeklyChallengeStyles = styled(ContentScreen)`
   @media (max-width: 750px) {
      .exercise-actions-cell {
         display: none;
      }

      td {
         line-height: 1.3;
         padding-bottom: 1em;
      }
   }
`;

const oneWeek = 1000 * 60 * 60 * 24 * 7;

const WeeklyChallenge = () => {
   const [weeks, setWeeks] = useState(null);

   const { search } = useLocation();

   const selectedDate = new URLSearchParams(search).get('selected');

   useLayoutEffect(() => {
      window.scrollTo({ top: 0 });
   }, [selectedDate]);

   let selectedWeek = null;
   let selectedWeekIndex;
   let previousWeekId;
   let nextWeekId;

   if (weeks) {
      selectedWeekIndex = weeks.findIndex(({ id }) => id === selectedDate);

      if (selectedWeekIndex >= 0) {
         selectedWeek = weeks[selectedWeekIndex];

         if (selectedWeekIndex > 0) {
            previousWeekId = weeks[selectedWeekIndex - 1].id;
         }

         if (selectedWeekIndex < weeks.length - 1) {
            nextWeekId = weeks[selectedWeekIndex + 1].id;
         }
      }
   }

   useEffect(() => {
      fetch(`${assetsUrl}/public/exercise/weekly-challenge/weeks.json`)
         .then((res) => res.json())
         .then((exerciseInfoResponse) => {
            setWeeks(
               exerciseInfoResponse
                  .map((week) => ({ ...week, id: week.date.substring(0, 10) }))
                  .filter(({ date }) => {
                     const dateDiff = Date.now() - new Date(date).getTime();
                     return dateDiff > 0;
                  })
                  .sort((a, b) => (a.date > b.date ? 1 : -1))
            );
         });
   }, []);

   if (weeks == null) {
      return <ShowSiteLoading />;
   }

   const currentWeekIndex = weeks.findIndex(({ date }) => {
      const dateDiff = Date.now() - new Date(date).getTime();
      return dateDiff > 0 && dateDiff <= oneWeek;
   });

   const currentWeek = weeks[currentWeekIndex];

   const displayedWeek = selectedWeek || currentWeek;
   const displayedIndex =
      selectedWeekIndex >= 0 ? selectedWeekIndex : currentWeekIndex;

   return (
      <WeeklyChallengeStyles className="narrow">
         <Title title="Weekly Challenge" />
         <h1>Weekly Challenge</h1>
         {displayedWeek && (
            <WeekDisplay>
               <h2>
                  Week {displayedIndex + 1}: {displayedWeek.title}
               </h2>
               <div>
                  <h3>Learn</h3>
                  <Button
                     as="a"
                     href={displayedWeek.tutorialUrl}
                     target="_blank"
                     rel="noopener"
                     style={{ marginRight: '1em' }}
                  >
                     Watch Tutorial
                  </Button>
               </div>
               <div>
                  <h3>Practice</h3>
                  {displayedWeek.practice.interactiveExercises &&
                     (displayedWeek.practice.interactiveExercises.length > 1 ? (
                        <div>
                           <h4>Interactive Exercises</h4>
                           <ul className="weekly-challenge-interactive-list">
                              {displayedWeek.practice.interactiveExercises.map(
                                 ({ path, title }) => (
                                    <li key={title}>
                                       <Button
                                          as={Link}
                                          className="button-link"
                                          to={`${path}&wcid=${displayedWeek.id}`}
                                       >
                                          <span className="weekly-challenge-play-link">
                                             <FiPlayCircle />
                                             <span>{title}</span>
                                          </span>
                                       </Button>
                                    </li>
                                 )
                              )}
                           </ul>
                        </div>
                     ) : (
                        <div>
                           <h4>Interactive Exercise</h4>

                           <Button
                              as={Link}
                              to={`${displayedWeek.practice.interactiveExercises[0].path}&wcid=${displayedWeek.id}`}
                              className="button-cta"
                           >
                              Practice Now
                           </Button>
                        </div>
                     ))}
                  {displayedWeek.practice.create && (
                     <div>
                        <h4>Create</h4>
                        <ul>
                           {displayedWeek.practice.create.map(({ title }) => (
                              <li key={title}>{title}</li>
                           ))}
                        </ul>
                     </div>
                  )}
                  {displayedWeek.practice.listen && (
                     <div>
                        <h4>Listen</h4>
                        <ul>
                           {displayedWeek.practice.listen.map(({ title }) => (
                              <li key={title}>{title}</li>
                           ))}
                        </ul>
                     </div>
                  )}
               </div>
               {selectedWeek && (
                  <div className="weekly-challenge-nav">
                     <p>
                        {previousWeekId && (
                           <Button
                              as={Link}
                              className="button-link"
                              to={`/app/weekly-challenge?selected=${previousWeekId}`}
                           >
                              Previous Week
                           </Button>
                        )}
                     </p>
                     <p>
                        <Button
                           as={Link}
                           className="button-link"
                           to="/app/weekly-challenge"
                        >
                           Current Week
                        </Button>
                     </p>
                     <p>
                        {nextWeekId && (
                           <Button
                              as={Link}
                              className="button-link"
                              to={`/app/weekly-challenge?selected=${nextWeekId}`}
                           >
                              Next Week
                           </Button>
                        )}
                     </p>
                  </div>
               )}
            </WeekDisplay>
         )}

         <h2>All Weeks</h2>

         <Table className="row-hover">
            <tbody>
               {weeks &&
                  weeks.map(({ title, id }, i) => (
                     <tr key={id}>
                        <td>
                           <Link to={`?selected=${id}`}>
                              Week {i + 1}: {title}
                           </Link>
                        </td>
                        <td className="exercise-actions-cell">
                           <Link className="icon-link" to={`?selected=${id}`}>
                              <FiInfo size="1.5em" />
                           </Link>
                        </td>
                     </tr>
                  ))}
               <tr>
                  <td>
                     Week {weeks.length + 1}: Available{' '}
                     {new Date(
                        new Date(weeks[weeks.length - 1].date).getTime() +
                           oneWeek
                     ).toLocaleDateString()}
                  </td>
                  <td className="exercise-actions-cell">
                     <FiLock size="1.5em" className="exercise-locked" />
                  </td>
               </tr>
            </tbody>
         </Table>
      </WeeklyChallengeStyles>
   );
};

export { WeeklyChallenge };
